import { UploadOutlined } from '@ant-design/icons';
import { Avatar, Button, Card, Form, Input, Popconfirm, Space, Switch, Tooltip, Upload, notification } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { SendTestMail } from '../Integrations/SendTestMail';
import { useDispatch } from 'react-redux';
import {
  doCreateSetting,
  doDeleteSetting,
  doGetSettings,
  doUpdateSetting,
} from '../../../../modules/settings/store/thunk';
import { useSelector } from 'react-redux';
import { clearState, selectAllSettings } from '../../../../modules/settings/store/slice';
import { ReactSVG } from 'react-svg';
import { convertModelToFormData } from '../../../../utils/common';
import { doSendTestMail } from '../../../../modules/sendgrid/store/thunk';
import { ROLES } from '../../../../constants';

export const Branding = ({ locationId, companyId }) => {
  const [form] = Form.useForm();

  const dispatch = useDispatch();
  const [on, setOn] = useState(false);
  const [hasBeenReset, setHasBeenReset] = useState(false);
  const initialState = {
    PRIMARY_COLOR: '',
    SECONDARY_COLOR: '',
    LOGO: '',
    HEADER: '',
  };

  const [brandingSettings, setBrandingSettings] = useState({ ...initialState });
  const [logoList, setLogoList] = useState([]);
  const [headerList, setHeaderList] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(true);

  const testMailTooltip = useMemo(() => (saved ? undefined : 'Please save settings first'), [saved]);

  const existing = useSelector((state) => selectAllSettings(state));

  const getValue = (key, value) => {
    if (['PRIMARY_COLOR', 'SECONDARY_COLOR'].includes(key) && value.startsWith('#')) return value.slice(1); // slice off # from color
    return value;
  };
  // intitialize form with existing values
  useEffect(() => {
    if (existing.length > 0) {
      if (existing[0].group === 'TNT_BRANDING') {
        setOn(true);
      }
      setBrandingSettings({
        ...initialState,
        ...existing.reduce((acc, c) => {
          form.setFieldValue(c.keyword, getValue(c.keyword, c.value));
          return {
            ...acc,
            [c.keyword]: getValue(c.keyword, c.value),
          };
        }, {}),
      });
      const logo = existing.find((s) => s.keyword === 'LOGO');
      const header = existing.find((s) => s.keyword === 'HEADER');
      if (logo) {
        setLogoList([
          {
            uid: logo.id + 'logo',
            status: 'done',
            url: logo.value,
            name: logo.value,
          },
        ]);
      }
      if (header) {
        setHeaderList([
          {
            uid: header.id + 'header',
            status: 'done',
            url: header.value,
            name: header.value,
          },
        ]);
      }
    } else {
      setHasBeenReset(true);
      setBrandingSettings({ ...initialState });
      form.resetFields();
      setOn(false);
      setLogoList([]);
      setHeaderList([]);
    }
  }, [existing]);

  const objectProps = useMemo(
    () => ({
      companyId: companyId || undefined,
      locationId: locationId || undefined,
    }),
    [companyId, locationId],
  );
  const userRole = useSelector((state) => state.auth.user.role);

  useEffect(() => {
    if (userRole === ROLES.ADMIN || objectProps.companyId) {
      dispatch(doGetSettings({ category: 'tnt-branding', ...objectProps }));
    }
    return () => dispatch(clearState()); // explicitly clear state on unmount
  }, [objectProps, userRole]);

  const normFile = (type) => (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return type === 'logo' ? logoList : headerList;
  };

  const handleChange = (type) => (info) => {
    let newFileList = [...info.fileList];
    const file = newFileList.slice(-1)[0];
    const isLt5M = (file?.size || 0) / 1024 / 1024 <= 5;
    if (!isLt5M) {
      notification.error({ message: 'Image must smaller than 5MB!' });
    } else {
      newFileList = newFileList.slice(-1);
      if (type === 'logo') {
        setLogoList(newFileList);
      } else if (type === 'header') {
        setHeaderList(newFileList);
      }
    }
    setSaved(false);
  };

  const cancel = async () => {
    const { payload } = await dispatch(doGetSettings({ category: 'tnt-branding', ...objectProps }));
    setSaved(true);
    if (payload?.settings?.length === 0) {
      setOn(false);
    }
  };

  const sendTest = async () => {
    const body = {
      ...brandingSettings,
      PRIMARY_COLOR: brandingSettings.PRIMARY_COLOR ? `#${brandingSettings.PRIMARY_COLOR}` : '',
      SECONDARY_COLOR: brandingSettings.SECONDARY_COLOR ? `#${brandingSettings.SECONDARY_COLOR}` : '',
    };
    dispatch(doSendTestMail(body));
  };

  const deleteSetting = (u) => {
    if (['PRIMARY_COLOR', 'SECONDARY_COLOR'].includes(u.key)) {
      return u.id && !u.value;
    }
    return u.id && (u.keyword === 'LOGO' ? !logoList[0] : !headerList[0]);
  };

  const hasValue = (u) => (u instanceof FormData && u.get('value')) || u.value;
  const hasId = (u) => (u instanceof FormData && u.get('id')) || u.id;

  const submit = async () => {
    setIsSaving(true);

    const logo = logoList.length > 0 ? logoList[0].originFileObj : undefined;
    const header = headerList?.length > 0 ? headerList[0].originFileObj : undefined;

    // construct param objects from input
    const updates = Object.entries(brandingSettings).map(([k, s]) => {
      const u = {
        id: existing.find((e) => k === e.keyword)?.id || null,
        keyword: k,
        value: ['PRIMARY_COLOR', 'SECONDARY_COLOR'].includes(k) ? `#${s.toUpperCase()}` : '', // send a color with hash and dont send the url value of images to the backend
        group: 'TNT_BRANDING',
        ...objectProps,
      };

      if (k === 'LOGO' && logo) {
        const formData = convertModelToFormData(u);
        formData.append('file', logo);
        return formData;
      }
      if (k === 'HEADER' && header) {
        const formData = convertModelToFormData(u);
        formData.append('file', header);
        return formData;
      }

      return u;
    });

    // send updates or creates to backend depending on their existance
    await Promise.all(
      updates.map(async (u) => {
        if (hasId(u) && hasValue(u)) {
          // if it existed and has a value => update it
          await dispatch(doUpdateSetting(u));
        } else if (deleteSetting(u)) {
          // if it has an id but does not have a value and did exist before, delete it
          await dispatch(doDeleteSetting(u));
        } else if (hasValue(u)) {
          // if it didn't exist => create it
          await dispatch(doCreateSetting(u));
        }
      }),
    );
    await cancel(); // to refetch and have everything in state
    setHasBeenReset(false);
    setSaved(true);
    setIsSaving(false);
  };

  const saveTooltip = useMemo(
    () => (hasBeenReset ? 'You currently use default branding' : 'Turn custom branding off and use Sparqle branding'),
    [hasBeenReset],
  );
  const turnOff = async () => {
    await Promise.all(
      existing.map(async (e) => {
        await dispatch(doDeleteSetting({ id: e.id }));
      }),
    );
    setHasBeenReset(true);
  };
  return !locationId && !companyId ? (
    <h4>Please select a company or location first</h4>
  ) : (
    <div>
      <h4>Branding</h4>
      <Space direction="vertical" size={16}>
        <Card
          title="Send branded track & trace emails"
          extra={
            <Switch
              onChange={(v) => {
                setOn(v);
                setHasBeenReset(false);
              }}
              checked={on}
            />
          }
          style={{ width: 400, border: 'none' }}
        >
          {on ? (
            <>
              <Form form={form} onFinish={submit} layout="vertical" requiredMark={false} colon={false}>
                <Tooltip title="Add your primary brand color" placement="right">
                  <Form.Item
                    label="Custom primary color"
                    value={brandingSettings.PRIMARY_COLOR}
                    initialValue={brandingSettings.PRIMARY_COLOR}
                    name="PRIMARY_COLOR"
                    rules={[
                      {
                        pattern: /^[\da-fA-F]{6}$/,
                        message: 'Please enter a valid hexadecimal color code eg: AA23FB',
                      },
                    ]}
                  >
                    <Input
                      placeholder=""
                      size="large"
                      prefix="#"
                      minLength={6}
                      maxLength={6}
                      onChange={(e) => {
                        setBrandingSettings({ ...brandingSettings, PRIMARY_COLOR: e.target.value });
                        setSaved(false);
                      }}
                      suffix={
                        <Avatar size="small" style={{ backgroundColor: `#${brandingSettings.PRIMARY_COLOR}` }}></Avatar>
                      }
                    />
                  </Form.Item>
                </Tooltip>
                <Tooltip title="Add your secondary brand color" placement="right">
                  <Form.Item
                    label="Custom secondary color"
                    value={brandingSettings.SECONDARY_COLOR}
                    initialValue={brandingSettings.SECONDARY_COLOR}
                    name="SECONDARY_COLOR"
                    rules={[
                      {
                        pattern: /^[\da-fA-F]{6}$/,
                        message: 'Please enter a valid hexadecimal color code eg: AA23FB',
                      },
                    ]}
                  >
                    <Input
                      placeholder=""
                      size="large"
                      prefix="#"
                      minLength={6}
                      maxLength={6}
                      onChange={(e) => {
                        setBrandingSettings({ ...brandingSettings, SECONDARY_COLOR: e.target.value });
                        setSaved(false);
                      }}
                      suffix={
                        <Avatar
                          size="small"
                          style={{ backgroundColor: `#${brandingSettings.SECONDARY_COLOR}` }}
                        ></Avatar>
                      }
                    />
                  </Form.Item>
                </Tooltip>
                <Tooltip title="Custom brand logo in all email communication to your customers." placement="right">
                  <Form.Item
                    label="Add brand logo in email"
                    name="LOGO"
                    validateStatus={!logoList || logoList.length === 0 ? 'error' : undefined}
                    getValueFromEvent={normFile('logo')}
                  >
                    <Upload.Dragger
                      multiple={false}
                      maxCount={1}
                      accept=".png, .jpg, .jpeg"
                      showUploadList={{
                        showPreviewIcon: false,
                        showDownloadIcon: false,
                        showRemoveIcon: true,
                      }}
                      customRequest={async ({ onSuccess, onError }) => {
                        try {
                          onSuccess('ok');
                        } catch (e) {
                          console.log(e);
                          onError();
                        }
                      }}
                      onRemove={() => setLogoList([])}
                      listType="picture-card"
                      fileList={logoList}
                      onChange={handleChange('logo')}
                    >
                      <p className="upload-text">Drag and drop to upload or</p>
                      <p className="upload-drag-icon">
                        Browse
                        <UploadOutlined />
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                </Tooltip>
                <Tooltip
                  title="Custom brand header image in all email communication to your customers."
                  placement="right"
                >
                  <Form.Item
                    label="Add brand header in email"
                    name="HEADER"
                    validateStatus={!headerList || headerList.length === 0 ? 'error' : undefined}
                    getValueFromEvent={normFile('header')}
                  >
                    <Upload.Dragger
                      multiple={false}
                      maxCount={1}
                      accept=".png, .jpg, .jpeg"
                      showUploadList={{
                        showPreviewIcon: false,
                        showDownloadIcon: false,
                        showRemoveIcon: true,
                      }}
                      customRequest={async ({ onSuccess, onError }) => {
                        try {
                          onSuccess('ok');
                        } catch (e) {
                          console.log(e);
                          onError();
                        }
                      }}
                      onRemove={() => setHeaderList([])}
                      listType="picture-card"
                      fileList={headerList}
                      onChange={handleChange('header')}
                    >
                      <p className="upload-text">Drag and drop to upload or</p>
                      <p className="upload-drag-icon">
                        Browse
                        <UploadOutlined />
                      </p>
                    </Upload.Dragger>
                  </Form.Item>
                </Tooltip>
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '32px' }}>
                  <Button key="cancel" size="small" onClick={cancel}>
                    Cancel
                  </Button>
                  <Button key="confirm" type="primary" size="small" htmlType="submit" loading={isSaving}>
                    {existing.length > 0 ? 'Update' : 'Add'} branding settings
                    <ReactSVG className="icon" src="/icons/done.svg" />
                  </Button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip title={testMailTooltip}>
                    <SendTestMail
                      send={sendTest}
                      style={{ marginTop: '12px' }}
                      size="small"
                      disabled={!saved}
                      tooltip={testMailTooltip}
                    />
                  </Tooltip>
                </div>
              </Form>
            </>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '32px' }}>
              <Button key="cancel" size="small" onClick={cancel}>
                Cancel
              </Button>
              <Popconfirm
                title="Are you sure to fall back to default Sparqle brading?"
                onConfirm={turnOff}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title={saveTooltip}>
                  <Button type="primary" size="small" disabled={hasBeenReset}>
                    Save and use default branding
                  </Button>
                </Tooltip>
              </Popconfirm>
            </div>
          )}
        </Card>
      </Space>
    </div>
  );
};
